import React, { useState } from 'react';

/* Validation */
import { Formik } from 'formik';
import { validationSchema } from './formConfig';
import CustomError from './CustomError';

/* Gjensidige Core 20 Imports */
import utils from '@gjensidige/core-utilities-css';

/* Gjensidige Core 30 Imports */
import { Alert } from '@gjensidige/core-components/lib/alerts/default';
import { Col, Row } from '@gjensidige/nci-core-grid';
import { Input } from '@gjensidige/core-components/lib/forms/input';
import { TextArea } from '@gjensidige/core-components/lib/forms/textarea';
import { Dropdown } from '@gjensidige/core-components/lib/forms/dropdown';
import { Text, Title } from '@gjensidige/nci-core-typography';
import { InputGroupSimple } from '@gjensidige/core-components/lib/forms/input-group-simple';
import { Checkbox } from '@gjensidige/core-components/lib/forms/checkbox';
import { Datepicker } from '@gjensidige/core-components/lib/datepicker';

/* API */
import axios from 'axios';
import { Endpoints } from '@internal/common';
import {Button} from "@gjensidige/core-components/lib/button";

export interface IProps {
  claimType: string;
}

const ClaimReportTheft: React.FC<IProps> = (props) => {
  const [formData, setFormData] = useState({
    claimType: props.claimType,
    companyName: '',
    cvrNumber: '',
    policyNumber: '',
    contactPersonNameAndEmployment: '',
    email: '',
    contactPersonPhoneNumber: '',
    theftLocation: '',
    sceneOfAccidentAddress: '',
    timeOfAccident: '',
    theftDiscovered: '',
    theftDetails: '',
    carSecurelyLocked: '',
    windowsClosed: '',
    alarmActivated: '',
    theftReportedDate: '',
    policeStation: '',
    whoReportedDamage: '',
    stolenItemsBelongToPolicyholder: '',
    stolenItemsBelongToOther: '',
    insuredByOthers: '',
    insuranceCompanyName: '',
    totalClaim: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);

  const sendFormData = () => {
    setApiError(false);
    axios
      .post(Endpoints.ClaimReportDIByggeri, formData)
      .then(() => setSubmitted(true))
      .catch(() => setApiError(true));
  };

  const dateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const value = target.value;
    setFormData({ ...formData, theftReportedDate: value });
  };

  const handleDropdownChange = (value: string) => {
    setFormData({ ...formData, theftLocation: value });
  };

  const theftLocationDropdown = [
    'Ejendom',
    'Bil',
    'Byggeplads',
    'Offentligt rum'
  ];

  const downloadExcelFile = () => {
    window.location.href =
      'https://www.gjensidige.dk/virksomhed/formularer/byg-tyveri/_attachment/638435?_download=true&_ts=17b628dfb52';
  };

  const todaysDate = new Date(new Date().setHours(0, 0, 0, 0));

  return (
    <>
      {submitted ? (
        <Alert
          alertType="success"
          showIcon
          title="Tak for din anmeldelse​"
          text="Vi sender dig en kvitteringsmail med informationerne fra din skadeanmeldelse.​"
          className={utils['mb-3']}
        />
      ) : (
        <>
          <div className={utils['pb-2']}>
            <Title tag="h5">Anmeldelse af tyveri</Title>
            <br />
            <Text size="p">
              Anmeld skaden til Politiet først og eftersend Politiets kvittering
              for anmeldelsen samt opgørelse over de stjålne genstande
              (excelark) til info@bygfs.dk. For elværktøj skal opgørelsen
              indeholde fabrikat, type, årgang og pris.
            </Text>
            <br />
            <Button variant={'primary'} onClick={downloadExcelFile}>
              Download excelark til opgørelse over de stjålne genstande
            </Button>
            <br />
            <br />
            <Text size="small">* Påkrævede felter</Text>
          </div>
          <Formik
            enableReinitialize
            initialValues={{
              companyName: formData.companyName,
              cvrNumber: formData.cvrNumber,
              policyNumber: formData.policyNumber,
              contactPersonNameAndEmployment:
                formData.contactPersonNameAndEmployment,
              email: formData.email,
              contactPersonPhoneNumber: formData.contactPersonPhoneNumber,
              theftLocation: formData.theftLocation,
              sceneOfAccidentAddress: formData.sceneOfAccidentAddress,
              timeOfAccident: formData.timeOfAccident,
              theftDiscovered: formData.theftDiscovered,
              theftDetails: formData.theftDetails,
              carSecurelyLocked: formData.carSecurelyLocked,
              windowsClosed: formData.windowsClosed,
              alarmActivated: formData.alarmActivated,
              theftReportedDate: formData.theftReportedDate,
              policeStation: formData.policeStation,
              whoReportedDamage: formData.whoReportedDamage,
              stolenItemsBelongToPolicyholder:
                formData.stolenItemsBelongToPolicyholder,
              stolenItemsBelongToOther: formData.stolenItemsBelongToOther,
              insuredByOthers: formData.insuredByOthers,
              insuranceCompanyName: formData.insuranceCompanyName,
              totalClaim: formData.totalClaim,
              consent: formData.consent
            }}
            validationSchema={validationSchema(formData)}
            onSubmit={() => {
              sendFormData();
            }}
          >
            {(props) => {
              const {
                handleSubmit,
                handleBlur,
                setFieldValue,
                touched,
                errors
              } = props;
              return (
                <form onSubmit={handleSubmit} className={utils['pb-3']}>
                  <Row>
                    <Col lg={12}>
                      <Input
                        id="companyName"
                        labelText="Virksomhedens navn *"
                        name="companyName"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('companyName', event.target.value);
                          setFormData({
                            ...formData,
                            companyName: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={!!(touched.companyName && errors.companyName)}
                        errorText={errors.companyName}
                        value={formData.companyName}
                      />
                      <Input
                        id="cvr-number"
                        labelText="CVR-nummer *"
                        name="cvrNumber"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('cvrNumber', event.target.value);
                          setFormData({
                            ...formData,
                            cvrNumber: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={!!(touched.cvrNumber && errors.cvrNumber)}
                        errorText={errors.cvrNumber}
                        value={formData.cvrNumber}
                      />
                      <Input
                        id="policy-number"
                        labelText="Policenummer *"
                        name="policyNumber"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('policyNumber', event.target.value);
                          setFormData({
                            ...formData,
                            policyNumber: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.policyNumber && errors.policyNumber)
                        }
                        errorText={errors.policyNumber}
                        value={formData.policyNumber}
                      />
                      <Input
                        id="contactPersonNameAndEmployment"
                        labelText="Navn og stilling (kontaktperson) *"
                        name="contactPersonNameAndEmployment"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue(
                            'contactPersonNameAndEmployment',
                            event.target.value
                          );
                          setFormData({
                            ...formData,
                            contactPersonNameAndEmployment: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.contactPersonNameAndEmployment &&
                            errors.contactPersonNameAndEmployment
                          )
                        }
                        errorText={errors.contactPersonNameAndEmployment}
                        value={formData.contactPersonNameAndEmployment}
                      />
                      <Input
                        id="email"
                        labelText="Email (kontaktperson) *"
                        name="email"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('email', event.target.value);
                          setFormData({
                            ...formData,
                            email: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={!!(touched.email && errors.email)}
                        errorText={errors.email}
                        value={formData.email}
                      />
                      <Input
                        id="contactPersonPhoneNumber"
                        labelText="Telefonnummer (kontaktperson) *"
                        name="contactPersonPhoneNumber"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue(
                            'contactPersonPhoneNumber',
                            event.target.value
                          );
                          setFormData({
                            ...formData,
                            contactPersonPhoneNumber: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.contactPersonPhoneNumber &&
                            errors.contactPersonPhoneNumber
                          )
                        }
                        errorText={errors.contactPersonPhoneNumber}
                        value={formData.contactPersonPhoneNumber}
                      />
                      <div className={utils['pb-2']}>
                        <Title tag="h5">Om tyveriet</Title>
                      </div>
                      <Dropdown
                        name="theftLocation"
                        id="theftLocation"
                        labelText="Tyveriet skete fra *"
                        className={utils['pb-2']}
                        options={theftLocationDropdown.map((option) => {
                          return { labelText: option, value: option };
                        })}
                        value={formData.theftLocation}
                        onChange={(value) => {
                          handleDropdownChange(value);
                          setFieldValue('typeOfDamage', value);
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.theftLocation && errors.theftLocation)
                        }
                        errorText={errors.theftLocation}
                      />
                      <TextArea
                        id="sceneOfAccidentAddress"
                        labelText="Skadestedets adresse *"
                        maxLength={500}
                        name="sceneOfAccidentAddress"
                        className={utils['pb-2']}
                        placeholder="Vejnavn og nummer, by samt postnummer"
                        value={formData.sceneOfAccidentAddress}
                        onChange={(
                          event: React.ChangeEvent<HTMLTextAreaElement>
                        ) => {
                          setFieldValue(
                            'sceneOfAccidentAddress',
                            event.target.value
                          );
                          setFormData({
                            ...formData,
                            sceneOfAccidentAddress: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.sceneOfAccidentAddress &&
                            errors.sceneOfAccidentAddress
                          )
                        }
                        errorText={errors.sceneOfAccidentAddress}
                      />
                      <Input
                        id="timeOfAccident"
                        labelText="Tidspunkt for tyveri *"
                        placeholder="Dato og klokkeslæt/tidsrum"
                        name="timeOfAccident"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('timeOfAccident', event.target.value);
                          setFormData({
                            ...formData,
                            timeOfAccident: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.timeOfAccident && errors.timeOfAccident)
                        }
                        errorText={errors.timeOfAccident}
                        value={formData.timeOfAccident}
                      />
                      <Input
                        id="theftDiscovered"
                        labelText="Hvornår blev tyveriet opdaget? *"
                        placeholder="Dato og klokkeslæt"
                        name="theftDiscovered"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('theftDiscovered', event.target.value);
                          setFormData({
                            ...formData,
                            theftDiscovered: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.theftDiscovered && errors.theftDiscovered)
                        }
                        errorText={errors.theftDiscovered}
                        value={formData.theftDiscovered}
                      />
                      <TextArea
                        id="theftDetails"
                        labelText="Hvordan mener du, tyveriet er foregået? *"
                        maxLength={500}
                        name="theftDetails"
                        className={utils['pb-2']}
                        placeholder="Beskriv gerne grundigt"
                        value={formData.theftDetails}
                        onChange={(
                          event: React.ChangeEvent<HTMLTextAreaElement>
                        ) => {
                          setFieldValue('theftDetails', event.target.value);
                          setFormData({
                            ...formData,
                            theftDetails: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.theftDetails && errors.theftDetails)
                        }
                        errorText={errors.theftDetails}
                      />
                      <InputGroupSimple
                        vertical
                        header="Hvis relevant: Var bilen forsvarligt aflåst? *"
                        id="carSecurelyLocked"
                        className={utils['pb-2']}
                        name="carSecurelyLocked"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            carSecurelyLocked: event.target.value
                          });
                          setFieldValue(
                            'carSecurelyLocked',
                            event.target.value
                          );
                        }}
                        options={[
                          {
                            label: 'Ja',
                            value: 'Ja'
                          },
                          {
                            label: 'Nej',
                            value: 'Nej'
                          }
                        ]}
                        type="radio"
                        value={formData.carSecurelyLocked}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.carSecurelyLocked &&
                            errors.carSecurelyLocked
                          )
                        }
                        errorText={errors.carSecurelyLocked}
                      />
                      <InputGroupSimple
                        vertical
                        header="Hvis relevant: Var alle vinduer helt lukkede? *"
                        id="windowsClosed"
                        className={utils['pb-2']}
                        name="windowsClosed"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            windowsClosed: event.target.value
                          });
                          setFieldValue('windowsClosed', event.target.value);
                        }}
                        options={[
                          {
                            label: 'Ja',
                            value: 'Ja'
                          },
                          {
                            label: 'Nej',
                            value: 'Nej'
                          }
                        ]}
                        type="radio"
                        value={formData.windowsClosed}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.windowsClosed && errors.windowsClosed)
                        }
                        errorText={errors.windowsClosed}
                      />
                      <InputGroupSimple
                        vertical
                        header="Hvis relevant: Blev tyverialarmen aktiveret? *"
                        id="alarmActivated"
                        className={utils['pb-2']}
                        name="alarmActivated"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            alarmActivated: event.target.value
                          });
                          setFieldValue('alarmActivated', event.target.value);
                        }}
                        options={[
                          {
                            label: 'Ja',
                            value: 'Ja'
                          },
                          {
                            label: 'Nej',
                            value: 'Nej'
                          }
                        ]}
                        type="radio"
                        value={formData.alarmActivated}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.alarmActivated && errors.alarmActivated)
                        }
                        errorText={errors.alarmActivated}
                      />
                      <Datepicker
                        labelText="Hvornår blev tyveriet anmeldt til politiet? *"
                        name="theftReportedDate"
                        id="theftReportedDate"
                        className={utils['pb-2']}
                        onChange={(event, value) => {
                          dateChange(event);
                          setFieldValue('theftReportedDate', value);
                        }}
                        invalid={
                          !!(
                            touched.theftReportedDate &&
                            errors.theftReportedDate
                          )
                        }
                        maxDate={todaysDate}
                        errorText={errors.theftReportedDate}
                        dateFormat={{
                          format: 'dd/MM/yyyy',
                          parseFormula: {
                            groups: { date: 1, month: 2, year: 3 },
                            regExp: /^(\d{1,2}).(\d{1,2}).(\d{4})$/
                          },
                          placeholder: 'dd/MM/yyyy'
                        }}
                        placeholder="DD/MM/YYYY"
                        value={formData.theftReportedDate}
                      />

                      <Input
                        id="policeStation"
                        labelText="Til hvilken politistation? *"
                        name="policeStation"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('policeStation', event.target.value);
                          setFormData({
                            ...formData,
                            policeStation: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.policeStation && errors.policeStation)
                        }
                        errorText={errors.policeStation}
                        value={formData.policeStation}
                      />
                      <Input
                        id="whoReportedDamage"
                        labelText="Hvem anmeldte skaden? *"
                        placeholder="Fulde navn"
                        name="whoReportedDamage"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue(
                            'whoReportedDamage',
                            event.target.value
                          );
                          setFormData({
                            ...formData,
                            whoReportedDamage: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.whoReportedDamage &&
                            errors.whoReportedDamage
                          )
                        }
                        errorText={errors.whoReportedDamage}
                        value={formData.whoReportedDamage}
                      />
                      <InputGroupSimple
                        vertical
                        header="Tilhører de stjålne/beskadigede genstande forsikringstageren? *"
                        id="stolenItemsBelongToPolicyholder"
                        className={utils['pb-2']}
                        name="stolenItemsBelongToPolicyholder"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            stolenItemsBelongToPolicyholder: event.target.value
                          });
                          setFieldValue(
                            'stolenItemsBelongToPolicyholder',
                            event.target.value
                          );
                        }}
                        options={[
                          {
                            label: 'Ja',
                            value: 'Ja'
                          },
                          {
                            label: 'Nej',
                            value: 'Nej'
                          }
                        ]}
                        type="radio"
                        value={formData.stolenItemsBelongToPolicyholder}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.stolenItemsBelongToPolicyholder &&
                            errors.stolenItemsBelongToPolicyholder
                          )
                        }
                        errorText={errors.stolenItemsBelongToPolicyholder}
                      />
                      {formData.stolenItemsBelongToPolicyholder === 'Nej' && (
                        <Input
                          id="stolenItemsBelongToOther"
                          labelText="Hvem tilhører de? *"
                          name="stolenItemsBelongToOther"
                          className={utils['pb-2']}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setFieldValue(
                              'stolenItemsBelongToOther',
                              event.target.value
                            );
                            setFormData({
                              ...formData,
                              stolenItemsBelongToOther: event.target.value
                            });
                          }}
                          onBlur={handleBlur}
                          invalid={
                            !!(
                              touched.stolenItemsBelongToOther &&
                              errors.stolenItemsBelongToOther
                            )
                          }
                          errorText={errors.stolenItemsBelongToOther}
                          value={formData.stolenItemsBelongToOther}
                        />
                      )}
                      <InputGroupSimple
                        vertical
                        header="Er der tegnet en forsikring mod samme skade hos noget andet selskab? *"
                        id="insuredByOthers"
                        className={utils['pb-2']}
                        name="insuredByOthers"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            insuredByOthers: event.target.value
                          });
                          setFieldValue('insuredByOthers', event.target.value);
                        }}
                        options={[
                          {
                            label: 'Ja',
                            value: 'Ja'
                          },
                          {
                            label: 'Nej',
                            value: 'Nej'
                          }
                        ]}
                        type="radio"
                        value={formData.insuredByOthers}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.insuredByOthers && errors.insuredByOthers)
                        }
                        errorText={errors.insuredByOthers}
                      />
                      {formData.insuredByOthers === 'Ja' && (
                        <Input
                          id="insuranceCompanyName"
                          labelText="Hvilket forsikringsselskab? *"
                          name="insuranceCompanyName"
                          className={utils['pb-2']}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setFieldValue(
                              'insuranceCompanyName',
                              event.target.value
                            );
                            setFormData({
                              ...formData,
                              insuranceCompanyName: event.target.value
                            });
                          }}
                          onBlur={handleBlur}
                          invalid={
                            !!(
                              touched.insuranceCompanyName &&
                              errors.insuranceCompanyName
                            )
                          }
                          errorText={errors.insuranceCompanyName}
                          value={formData.insuranceCompanyName}
                        />
                      )}
                      <div className={utils['pb-2']}>
                        <Title tag="h5">Det beskadigede eller stjålne</Title>
                      </div>
                      <Input
                        id="totalClaim"
                        labelText="Angiv det totale erstatningskrav *"
                        helpText="Angiv om det er inkl. eller ekskl. moms"
                        helpTextOpen
                        name="totalClaim"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('totalClaim', event.target.value);
                          setFormData({
                            ...formData,
                            totalClaim: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={!!(touched.totalClaim && errors.totalClaim)}
                        errorText={errors.totalClaim}
                        value={formData.totalClaim}
                      />
                      <div className={utils['pb-2']}>
                        <Title tag="h5">Dokumentation</Title>
                      </div>
                      <Text size="p">
                        Når du har trukket Anmeld Skade nedenfor, modtager du en
                        kvittering, hvor der er vedhæftet et regneark. Dette
                        regneark skal du udfylde og sende tilbage info@bygfs.dk.
                        Det kan også hentes på kvitteringssiden. Fakturaer og
                        anden dokumentation skal også sendes via mail.
                      </Text>
                      <br />
                      <Checkbox
                        id="consent-id"
                        label="Jeg erklærer herved, at foranstående oplysninger er i overensstemmelse med de faktiske forhold, og at jeg vil yde selskabet enhver assistance i forbindelse med skaden. *"
                        name="consent"
                        type="checkbox"
                        value="consent"
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('consent', event.target.checked);
                          setFormData({
                            ...formData,
                            consent: event.target.checked
                          });
                        }}
                        onBlur={handleBlur}
                        className={utils['pb-2']}
                        topAlignInput
                        checked={formData.consent}
                      />
                      {touched.consent && errors.consent && (
                        <>
                          <br />
                          <CustomError errorText={errors.consent} />
                        </>
                      )}
                      <Button variant={'primary'} type="submit" flexible>
                        Anmeld Skade
                      </Button>
                    </Col>
                  </Row>
                </form>
              );
            }}
          </Formik>
        </>
      )}
      {apiError && (
        <Alert
          alertType="error"
          showIcon
          text="Der skete en fejl ved indsendelse. Prøv venligst igen senere"
          title="Der skete en fejl."
          className={utils['mb-3']}
        />
      )}
    </>
  );
};

export default ClaimReportTheft;
